// Kontot: passkey, notiser och tysta tider, och utloggningen. Frågorna efter inloggningen
// (notiser och passkey) visas också här, en gång.
//
// b16: de tysta tiderna ändras här (BK13, förval 23–06), och ändringen står i platsernas logg.

import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { useEffect, useState } from "preact/hooks";
import { api, Felsvar, type Jag, type Konfig } from "../api.ts";
import { Kort } from "../komponenter.tsx";
import {
  harFragat,
  markeraFragat,
  type Notislage,
  notislage,
  slaAvNotiser,
  slaPaNotiser,
} from "../notiser.ts";

const PASSKEY_FRAGAT = "batkoll.passkeyfraga";

function timmar(t: { fran: number; till: number }): string {
  const tt = (h: number) => String(h).padStart(2, "0");
  return `${tt(t.fran)}–${tt(t.till)}`;
}

/** b16: de tysta tiderna, två hela timmar. Används också i guidens fråga 6. */
export function Tystatider(props: {
  varde: { fran: number; till: number };
  satt: (v: { fran: number; till: number }) => void;
}) {
  const val = (namn: string, falt: "fran" | "till") => (
    <label>
      {namn}
      <select
        value={String(props.varde[falt])}
        onChange={(e) =>
          props.satt({ ...props.varde, [falt]: Number((e.target as HTMLSelectElement).value) })
        }
      >
        {Array.from({ length: 24 }, (_, h) => (
          <option key={h} value={String(h)}>
            {String(h).padStart(2, "0")}
          </option>
        ))}
      </select>
    </label>
  );
  return (
    <div class="faltrad">
      {val("Tysta från kl.", "fran")}
      {val("till kl.", "till")}
    </div>
  );
}

const HEMSKARM =
  "På iPhone och iPad fungerar notiser bara när Båtkoll ligger på hemskärmen. Öppna Båtkoll i Safari, tryck på Dela och välj Lägg till på hemskärmen. Öppna sedan Båtkoll från hemskärmen.";

function Notisstatus(props: { lage: Notislage | null }) {
  switch (props.lage) {
    case "pa":
      return <p>Notiserna är på för den här enheten.</p>;
    case "av":
      return <p>Notiserna är av för den här enheten.</p>;
    case "nekad":
      return <p>Notiserna är blockerade i webbläsaren. Tillåt dem i webbläsarens inställningar.</p>;
    case "hemskarm":
      return <p>{HEMSKARM}</p>;
    case "kan-inte":
      return <p>Den här webbläsaren kan inte ta emot notiser.</p>;
    default:
      return null;
  }
}

/** Frågan om notiser, en gång efter inloggningen, med en kort förklaring. */
export function Notisfraga(props: { konfig: Konfig; jag: Jag }) {
  const [lage, setLage] = useState<Notislage | null>(null);
  const [synlig, setSynlig] = useState(!harFragat());
  const [fel, setFel] = useState<string | null>(null);

  useEffect(() => {
    void notislage().then(setLage);
  }, []);

  if (!synlig || !props.konfig.vapidPublik) return null;
  if (lage === "pa") {
    return (
      <Kort rubrik="Notiser" bred>
        <p class="meddelande" role="status">
          Notiserna är på.
        </p>
      </Kort>
    );
  }
  if (lage !== "av" && lage !== "hemskarm") return null;

  const stang = () => {
    markeraFragat();
    setSynlig(false);
  };

  async function sagJa() {
    setFel(null);
    try {
      const nytt = await slaPaNotiser(props.konfig.vapidPublik ?? "");
      setLage(nytt);
      markeraFragat();
      if (nytt === "pa") setTimeout(() => setSynlig(false), 2500);
    } catch {
      setFel("Notiserna gick inte att slå på. Försök igen från Konto.");
    }
  }

  return (
    <Kort rubrik="Notiser" bred>
      {lage === "hemskarm" ? (
        <>
          <p class="meddelande">{HEMSKARM}</p>
          <div class="knapprad">
            <button type="button" class="knapp-andra" onClick={stang}>
              Okej
            </button>
          </div>
        </>
      ) : (
        <>
          <p class="meddelande">
            Båtkoll kan skicka en notis när något händer ombord, till exempel när landströmmen
            försvinner. Akut går alltid fram. Varningar kommer utan ljud mellan{" "}
            {timmar(props.jag.tystaTider)}.
          </p>
          {fel && (
            <p class="fel" role="alert">
              {fel}
            </p>
          )}
          <div class="knapprad">
            <button type="button" class="knapp-huvud" onClick={sagJa}>
              Slå på notiser
            </button>
            <button type="button" class="knapp-andra" onClick={stang}>
              Inte nu
            </button>
          </div>
        </>
      )}
    </Kort>
  );
}

/** Erbjudandet om en passkey, en gång efter den första inloggningen. */
export function Passkeyfraga(props: { jag: Jag; uppdatera: () => Promise<void> }) {
  const [synlig, setSynlig] = useState(() => {
    try {
      return localStorage.getItem(PASSKEY_FRAGAT) === null;
    } catch {
      return false;
    }
  });
  const [fel, setFel] = useState<string | null>(null);
  if (!synlig || props.jag.passkeys > 0 || props.jag.prov || !browserSupportsWebAuthn())
    return null;

  const stang = () => {
    try {
      localStorage.setItem(PASSKEY_FRAGAT, new Date().toISOString());
    } catch {}
    setSynlig(false);
  };

  async function laggTill() {
    setFel(null);
    try {
      await api.laggTillPasskey();
      stang();
      await props.uppdatera();
    } catch {
      setFel("Passkeyn gick inte att lägga till. Du kan försöka igen från Konto.");
    }
  }

  return (
    <Kort rubrik="Logga in snabbare" bred>
      <p class="meddelande">
        Med en passkey loggar du in med ansiktet, fingret eller telefonens kod, utan mejl.
      </p>
      {fel && (
        <p class="fel" role="alert">
          {fel}
        </p>
      )}
      <div class="knapprad">
        <button type="button" class="knapp-huvud" onClick={laggTill}>
          Lägg till passkey
        </button>
        <button type="button" class="knapp-andra" onClick={stang}>
          Inte nu
        </button>
      </div>
    </Kort>
  );
}

export function Kontovy(props: {
  jag: Jag;
  konfig: Konfig;
  uppdatera: () => Promise<void>;
  loggaUt: () => void;
}) {
  const { jag, konfig } = props;
  const [lage, setLage] = useState<Notislage | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [fel, setFel] = useState<string | null>(null);
  const [tysta, setTysta] = useState(jag.tystaTider);

  useEffect(() => {
    void notislage().then(setLage);
  }, []);

  async function sparaTysta() {
    setFel(null);
    setStatus(null);
    try {
      const r = await api.tystaTider(tysta.fran, tysta.till);
      setStatus(
        r.andrad
          ? `De tysta tiderna är nu ${timmar(r.tystaTider)}. Ändringen står i platsernas logg.`
          : "De tysta tiderna är oförändrade.",
      );
      await props.uppdatera();
    } catch (e) {
      setFel(e instanceof Felsvar ? e.message : "De tysta tiderna gick inte att spara.");
    }
  }

  async function passkey() {
    setFel(null);
    setStatus(null);
    try {
      await api.laggTillPasskey();
      setStatus("Passkeyn är tillagd.");
      await props.uppdatera();
    } catch {
      setFel("Passkeyn gick inte att lägga till.");
    }
  }

  async function notiser() {
    setFel(null);
    try {
      if (lage === "pa") {
        await slaAvNotiser();
        setLage("av");
      } else {
        setLage(await slaPaNotiser(konfig.vapidPublik ?? ""));
      }
      await props.uppdatera();
    } catch {
      setFel("Notiserna gick inte att ändra.");
    }
  }

  return (
    <div class="rutnat">
      <Kort rubrik="Kontot">
        <p class="meddelande">{jag.epost}</p>
        <button type="button" class="knapp-andra" onClick={props.loggaUt}>
          Logga ut
        </button>
      </Kort>
      <Kort rubrik="Passkey">
        <p class="meddelande">
          {jag.passkeys === 0
            ? "Ingen passkey än. Med en passkey loggar du in utan mejl."
            : jag.passkeys === 1
              ? "Du har en passkey."
              : `Du har ${jag.passkeys} passkeys.`}
        </p>
        {browserSupportsWebAuthn() && (
          <button type="button" class="knapp-andra" onClick={passkey}>
            Lägg till passkey
          </button>
        )}
      </Kort>
      <Kort rubrik="Notiser">
        <Notisstatus lage={lage} />
        {konfig.vapidPublik && (lage === "av" || lage === "pa") && (
          <button type="button" class="knapp-andra" onClick={notiser}>
            {lage === "pa" ? "Slå av notiser" : "Slå på notiser"}
          </button>
        )}
      </Kort>
      <Kort rubrik="Tysta tider">
        <p class="liten">
          Tysta tider: {timmar(jag.tystaTider)}. Då kommer varningar utan ljud. Akut går alltid
          fram.
        </p>
        <form
          class="formular"
          onSubmit={(e) => {
            e.preventDefault();
            void sparaTysta();
          }}
        >
          <Tystatider varde={tysta} satt={setTysta} />
          <button type="submit" class="knapp-andra">
            Spara de tysta tiderna
          </button>
        </form>
      </Kort>
      {(status || fel) && (
        <p class={fel ? "fel" : "ok-text"} role={fel ? "alert" : "status"}>
          {fel ?? status}
        </p>
      )}
    </div>
  );
}
